// taken from https://github.com/mo/abortcontroller-polyfill/blob/b5c6843c03901088fbdd76df7ab775cb2e0cf2c4/src/abortcontroller.js
// MIT License, Copyright (c) 2017 molsson

import { defineEventHandlers, TypedEventTarget } from "./event-target";
import { patchClass } from "./utils/class";

import { RuntimeError, softFail } from "../shared-utils/errors";

type AbortEvent = Event & { type: "abort"; reason?: unknown };

type AbortSignalEventMap = {
  abort: AbortEvent;
};

export class AbortSignal extends TypedEventTarget<AbortSignalEventMap> {
  public aborted!: boolean;
  public reason!: unknown | undefined;

  constructor() {
    super();

    // Compared to assignment, Object.defineProperty makes properties non-enumerable by default and
    // we want Object.keys(new AbortController().signal) to be [] for compat with the native impl
    Object.defineProperty(this, "aborted", {
      value: false,
      writable: true,
      configurable: true,
    });
    Object.defineProperty(this, "reason", {
      value: undefined,
      writable: true,
      configurable: true,
    });
  }
  toString() {
    return "[object AbortSignal]";
  }
  dispatchEvent(event: AbortEvent | Event): boolean {
    if (event.type === "abort") {
      this.aborted = true;
      this.reason = (event as AbortEvent).reason;
    }

    return super.dispatchEvent(event);
  }

  /**
   * @see {@link https://developer.mozilla.org/zh-CN/docs/Web/API/AbortSignal/throwIfAborted}
   */
  throwIfAborted() {
    const { aborted, reason = "Aborted" } = this;

    if (!aborted) return;

    throw reason;
  }

  static abort(reason?: unknown) {
    const controller = new globalThis.AbortController();
    controller.abort(reason);
    return controller.signal;
  }

  /**
   * @see {@link https://developer.mozilla.org/zh-CN/docs/Web/API/AbortSignal/timeout_static}
   * @param {number} time The "active" time in milliseconds before the returned {@link AbortSignal} will abort.
   *                      The value must be within range of 0 and {@link Number.MAX_SAFE_INTEGER}.
   * @returns {AbortSignal} The signal will abort with its {@link AbortSignal.reason} property set to a `TimeoutError` {@link DOMException} on timeout,
   *                        or an `AbortError` {@link DOMException} if the operation was user-triggered.
   */
  static timeout(time: number) {
    const controller = new globalThis.AbortController();

    setTimeout(
      () =>
        controller.abort(
          new DOMException(`signal timed out in ${time}ms`, "TimeoutError")
        ),
      time
    );

    return controller.signal;
  }

  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/any_static}
   * @param {Iterable<AbortSignal>} iterable An {@link Iterable} (such as an {@link Array}) of abort signals.
   * @returns {AbortSignal} - **Already aborted**, if any of the abort signals given is already aborted.
   *                          The returned {@link AbortSignal}'s reason will be already set to the `reason` of the first abort signal that was already aborted.
   *                        - **Asynchronously aborted**, when any abort signal in `iterable` aborts.
   *                          The `reason` will be set to the reason of the first abort signal that is aborted.
   */
  static any(iterable: Iterable<AbortSignal>) {
    // materialize the iterable to avoid multiple enumeration
    const signals = [...iterable];
    const controller = new globalThis.AbortController();
    function abort(this: AbortSignal) {
      if (!controller.signal.aborted) {
        controller.abort(this.reason);
      }
      clean();
    }
    function clean() {
      for (const signal of signals) signal.removeEventListener("abort", abort);
    }
    for (const signal of signals)
      if (signal.aborted) {
        controller.abort(signal.reason);
        clean();
        break;
      } else signal.addEventListener("abort", abort);

    return controller.signal;
  }
}

defineEventHandlers(AbortSignal, "abort");

export class AbortController {
  public readonly signal!: AbortSignal;

  constructor() {
    // Compared to assignment, Object.defineProperty makes properties non-enumerable by default and
    // we want Object.keys(new AbortController()) to be [] for compat with the native impl
    Object.defineProperty(this, "signal", {
      value: new AbortSignal(),
      writable: true,
      configurable: true,
    });
  }
  abort(
    reason: unknown = new DOMException(
      "signal is aborted without reason",
      "AbortError"
    )
  ) {
    // not in spec, but could help find logic errors
    // where abort is called multiple times on the same controller
    if (this.signal.aborted) {
      softFail(
        RuntimeError,
        "AbortController: cannot abort a signal that has already been aborted"
      );
      return;
    }
    const event = new Event("abort") as AbortEvent;
    event.reason = reason;
    this.signal.dispatchEvent(event);
  }
  toString() {
    return "[object AbortController]";
  }
}

// a usable AbortSignal implementation for us needs at least the reason property
// when it's not there, replace the whole implementation with the polyfill
// see: https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal
// - Safari 15.4
// - Chrome 98
// - Firefox 97
if (
  !globalThis.AbortSignal ||
  !Object.hasOwn(globalThis.AbortSignal.prototype, "reason")
) {
  // @ts-expect-error technically not the same type, but we want to replace the global implementation with our polyfill
  patchClass(import.meta.hot, "AbortSignal", () => AbortSignal);
  // @ts-expect-error technically not the same type, but we want to replace the global implementation with our polyfill
  patchClass(import.meta.hot, "AbortController", () => AbortController);
}

/** since:
 * - Safari 16
 * - Chrome 124
 * - Firefox 100
 */
if (!globalThis.AbortSignal.timeout) {
  globalThis.AbortSignal.timeout = AbortSignal.timeout;
}

/** since:
 * - Safari 17.4
 * - Chrome 116
 * - Firefox 124
 */
if (!globalThis.AbortSignal.any) {
  // @ts-expect-error our polyfill takes any Iterable, structurally compatible with the lib's signature
  globalThis.AbortSignal.any = AbortSignal.any;
}
